import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useBluetoothPrinter } from './bluetooth';
import { useInitWifiPrinter } from './wifi';
import printerStore from '~/store/printer';
import {
  PrintApi,
  PrinterSettings,
  PrinterType,
  PrintFunction,
  PrintResponse,
} from '~/types/printer';

export type PrinterContextValue = {
  settings: PrinterSettings;
  saveSettings: (settings: PrinterSettings) => void;
  bluetooth: {
    loading: boolean;
    device: any;
    connectNew: () => void;
    disconnect: () => void;
  };
  isReady: boolean;
  print: PrintFunction;
};

const PrinterContext = createContext<PrinterContextValue>({
  settings: { type: PrinterType.Bluetooth },
  saveSettings: () => {},
  bluetooth: {
    loading: false,
    device: {},
    connectNew: () => {},
    disconnect: () => {},
  },
  isReady: false,
  print: () => Promise.resolve(),
});

export const PrinterProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState(printerStore.getSettings());

  const saveSettings = useCallback((settings: PrinterSettings) => {
    setSettings(settings);
    printerStore.saveSettings(settings);
  }, []);

  const {
    print: bluetoothPrint,
    loading,
    connectNew,
    disconnect,
    device,
  } = useBluetoothPrinter({ settings, saveSettings });
  useInitWifiPrinter({ settings, saveSettings });

  const value: PrinterContextValue = useMemo(() => {
    return {
      settings,
      bluetooth: {
        loading,
        device,
        connectNew,
        disconnect,
      },
      saveSettings,
      ...(() => {
        const isBluetooth = settings.type === PrinterType.Bluetooth;
        const isReady = isBluetooth
          ? Boolean(bluetoothPrint)
          : Boolean(settings.printerId);

        const print: PrintFunction = async (api: PrintApi) => {
          if (!isReady) {
            if (isBluetooth) {
              throw new Error('Bluetooth printer belum connect');
            } else {
              throw new Error('Wi-Fi Printer belum disetup');
            }
          }

          const { encodedBluetoothPayload } = await api({
            printMethod: settings.type,
            ...(!isBluetooth ? { printerId: settings.printerId } : {}),
          });
          if (isBluetooth) {
            if (encodedBluetoothPayload == null)
              throw new Error('Empty response from print server');
            return bluetoothPrint?.(encodedBluetoothPayload);
          }
        };

        return { isReady, print };
      })(),
    };
  }, [
    settings,
    saveSettings,
    device,
    connectNew,
    disconnect,
    bluetoothPrint,
    loading,
  ]);

  return (
    <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>
  );
};

export const usePrinter = () => {
  return useContext(PrinterContext);
};
